/**
 * formBridge – transitional seam between a membership view (apply / edit /
 * review / pdf) and the step components that were extracted from it.
 *
 * The view calls `provide: provideFormContext`, so every step component can
 * reach the view instance. `bridge({...})` then mirrors exactly the view
 * members that one step's template uses (state, computed, methods, `v$`).
 *
 *  - state    : view data, read AND write (v-model / inline assignments work)
 *  - readonly : view computed properties, read only
 *  - methods  : view methods, executed on the view instance (same `this`)
 *  - validate : mirrors the view's Vuelidate instance as `v$`
 *
 * Nothing about the original logic changes: the step still reads and writes
 * the view's own state, and the view still owns validation, loading, saving.
 * Later this seam can be swapped for explicit props / emits one step at a time.
 */
export const FORM_CTX_KEY = "membershipFormCtx";

// Used as the `provide` option of a view: `provide: provideFormContext`.
export function provideFormContext() {
  return { [FORM_CTX_KEY]: this };
}

export function bridge({
  state = [],
  readonly = [],
  methods = [],
  validate = false,
} = {}) {
  const computed = {};
  state.forEach((name) => {
    computed[name] = {
      get() {
        return this[FORM_CTX_KEY][name];
      },
      set(value) {
        this[FORM_CTX_KEY][name] = value;
      },
    };
  });
  readonly.forEach((name) => {
    computed[name] = function () {
      return this[FORM_CTX_KEY][name];
    };
  });
  if (validate) {
    computed.v$ = function () {
      return this[FORM_CTX_KEY].v$;
    };
  }
  const bridged = {};
  methods.forEach((name) => {
    bridged[name] = function (...args) {
      return this[FORM_CTX_KEY][name](...args);
    };
  });
  return { inject: [FORM_CTX_KEY], computed, methods: bridged };
}
